import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ProjectsPage from "./pages/ProjectsPage";
import EpisodeEditorPage from "./pages/EpisodeEditorPage";
import SubjectLibraryPage from "./pages/SubjectLibraryPage";
import ProjectPage from "./pages/ProjectPage";
import ProjectSettingsPage from "./pages/ProjectSettingsPage";
import ProjectScriptPage from "./pages/ProjectScriptPage";
import ProjectEpisodesPage from "./pages/ProjectEpisodesPage";
import ScriptGenerationPage from "./pages/ScriptGenerationPage";
import BatchResourceLoadingPage from "./pages/BatchResourceLoadingPage";
import ImageGenerationsPage from "./pages/ImageGenerationsPage";
import ImageEditsPage from "./pages/ImageEditsPage";
import AudioGenerationsPage from "./pages/AudioGenerationsPage";
import MusicGenerationsPage from "./pages/MusicGenerationsPage";
import MusicEditsPage from "./pages/MusicEditsPage";
import AudioEditsPage from "./pages/AudioEditsPage";
import SoundEffectPage from "./pages/SoundEffectPage";
import VideoGenerationsPage from "./pages/VideoGenerationsPage";
import VideoLipsyncsPage from "./pages/VideoLipsyncsPage";
import CoinsPage from "./pages/CoinsPage";
import CreativeChatPage from "./pages/CreativeChatPage";
import AvatarTrainingsPage from "./pages/AvatarTrainingsPage";
import AvatarVideosPage from "./pages/AvatarVideosPage";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import GlobalLoading from "./components/GlobalLoading";
import ToastContainer from "./components/ToastContainer";
import { DialogProvider } from "./hooks/useDialog";
import { useGlobalLoading } from "./stores/globalLoadingStore";
import { WebSocketProvider } from "./services/websocket";
import { useAuthStore } from "./stores/authStore";
import { useCoinBalanceSync } from "./hooks/useCoinBalanceSync";
import { useTheme } from "./hooks/useTheme";
import { useTenantStore } from "./stores/tenantStore";
import WithProjectSubscription from "./components/WithProjectSubscription";
import WithEpisodeSubscription from "./components/WithEpisodeSubscription";
import { Outlet } from "react-router-dom";
import "./index.css";

// 租户初始化组件：应用挂载时加载租户信息
const TenantInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, loadTenantInfo, name } = useTenantStore();

  React.useEffect(() => {
    loadTenantInfo();
  }, [loadTenantInfo]);

  React.useEffect(() => {
    if (isLoaded && name) {
      document.title = name;
    }
  }, [isLoaded, name]);

  if (!isLoaded) {
    return (
      <div className="h-screen bg-bg-page flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
};

// 内部组件，用于在 WebSocketProvider 内部使用 hooks
const WebSocketSyncManager: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // 订阅剧点余额变化
  useCoinBalanceSync();
  return <>{children}</>;
};

// 旧路径重定向辅助组件（Navigate 不支持动态参数替换）
const RedirectEpisodeEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/episode/${id}/editor`} replace />;
};
const RedirectScriptGeneration: React.FC = () => {
  const { episodeId } = useParams<{ episodeId: string }>();
  return <Navigate to={`/episode/${episodeId}/script-generation`} replace />;
};
const RedirectBatchResourceLoading: React.FC = () => {
  const { episodeId } = useParams<{ episodeId: string }>();
  return <Navigate to={`/episode/${episodeId}/batch-resource-loading`} replace />;
};

const App: React.FC = () => {
  const { isLoading, message } = useGlobalLoading();
  const { token } = useAuthStore();
  useTheme(); // 初始化主题，监听系统变化

  return (
    <DialogProvider>
      <TenantInitializer>
        <WebSocketProvider authToken={token || ""} autoConnect={!!token}>
          <WebSocketSyncManager>
            <Router>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                {/* 新的单页面路由 */}
                <Route
                  path="project/:projectId"
                  element={
                    <ProtectedRoute>
                      <WithProjectSubscription>
                        <ProjectPage />
                      </WithProjectSubscription>
                    </ProtectedRoute>
                  }
                />
                {/* 旧路由重定向，保持兼容 */}
                <Route
                  path="project/:projectId/settings"
                  element={
                    <ProtectedRoute>
                      <WithProjectSubscription>
                        <ProjectSettingsPage />
                      </WithProjectSubscription>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="project/:projectId/script"
                  element={
                    <ProtectedRoute>
                      <WithProjectSubscription>
                        <ProjectScriptPage />
                      </WithProjectSubscription>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="project/:projectId/episodes"
                  element={
                    <ProtectedRoute>
                      <WithProjectSubscription>
                        <ProjectEpisodesPage />
                      </WithProjectSubscription>
                    </ProtectedRoute>
                  }
                />
                {/* 剧集父路由：统一订阅 episode WebSocket */}
                <Route
                  path="episode/:episodeId"
                  element={
                    <ProtectedRoute>
                      <WithEpisodeSubscription>
                        <Outlet />
                      </WithEpisodeSubscription>
                    </ProtectedRoute>
                  }
                >
                  <Route path="editor" element={<EpisodeEditorPage />} />
                  <Route path="script-generation" element={<ScriptGenerationPage />} />
                  <Route path="batch-resource-loading" element={<BatchResourceLoadingPage />} />
                </Route>
                {/* 旧路径重定向，保持兼容 */}
                <Route path="episode-editor/:id" element={<RedirectEpisodeEditor />} />
                <Route path="script-generation/:episodeId" element={<RedirectScriptGeneration />} />
                <Route path="batch-resource-loading/:episodeId" element={<RedirectBatchResourceLoading />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route
                    path="subject-library"
                    element={<SubjectLibraryPage />}
                  />
                  <Route path="voice-library" element={<Navigate to="/subject-library" replace />} />
                  <Route
                    path="creative-chat"
                    element={<CreativeChatPage />}
                  />
                  <Route
                    path="image-generation"
                    element={<ImageGenerationsPage />}
                  />
                  <Route path="image-edit" element={<ImageEditsPage />} />
                  <Route
                    path="audio-generation"
                    element={<AudioGenerationsPage />}
                  />
                  <Route
                    path="music-generation"
                    element={<MusicGenerationsPage />}
                  />
                  <Route path="music-edit" element={<MusicEditsPage />} />
                  <Route path="audio-edit" element={<AudioEditsPage />} />
                  <Route path="sound-effect" element={<SoundEffectPage />} />
                  <Route
                    path="video-generation"
                    element={<VideoGenerationsPage />}
                  />
                  <Route path="video-lipsync" element={<VideoLipsyncsPage />} />
                  <Route path="avatar-training" element={<AvatarTrainingsPage />} />
                  <Route path="avatar-video" element={<AvatarVideosPage />} />
                  <Route path="coins" element={<CoinsPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route
                    path="change-password"
                    element={<ChangePasswordPage />}
                  />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <GlobalLoading isOpen={isLoading} message={message} />
              <ToastContainer />
              {/* {token && <WebSocketDebugPanel />} */}
            </Router>
          </WebSocketSyncManager>
        </WebSocketProvider>
      </TenantInitializer>
    </DialogProvider>
  );
};

export default App;
